import { useRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type TabItem<T extends string> = { id: T; label: ReactNode };

type TabsProps<T extends string> = {
  label: string;
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Prefix for tab/panel ids; panels use `panelId(prefix, id)`. */
  idPrefix: string;
  tone?: 'light' | 'dark';
  /** Panel id each tab controls; defaults to `panelId(idPrefix, id)`. */
  controls?: (id: T) => string;
  className?: string;
};

export const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
export const panelId = (prefix: string, id: string) => `${prefix}-panel-${id}`;

/** Underline tabs with roving tabindex and arrow/Home/End keys (WAI-ARIA tabs pattern). */
export function Tabs<T extends string>({
  label,
  items,
  value,
  onChange,
  idPrefix,
  tone = 'light',
  controls,
  className,
}: TabsProps<T>) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  function onKeyDown(e: React.KeyboardEvent) {
    const index = items.findIndex((i) => i.id === value);
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else return;
    e.preventDefault();
    const id = items[next]!.id;
    onChange(id);
    refs.current.get(id)?.focus();
  }

  return (
    <div role="tablist" aria-label={label} className={cn('flex', className)}>
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(el) => {
              if (el) refs.current.set(item.id, el);
              else refs.current.delete(item.id);
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, item.id)}
            aria-selected={selected}
            aria-controls={controls ? controls(item.id) : panelId(idPrefix, item.id)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={onKeyDown}
            className={cn(
              'min-h-11 cursor-pointer border-0 border-b-2 bg-transparent px-4 text-sm',
              tone === 'light'
                ? selected
                  ? 'border-ink font-semibold text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
                : selected
                  ? 'border-code-text bg-code-bg font-mono text-meta text-code-text'
                  : 'border-transparent font-mono text-meta text-code-text-muted hover:text-code-text',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
